/*
  Reusable chat scroll management hook.
  - Keeps viewport anchored when prepending older messages.
  - Auto-scrolls to bottom only if the user is already at/near bottom.
  - Exposes unread count while away from bottom and a scrollToBottom action.
*/
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MESSAGE_CONTAINER_CLASSNAME } from "../library/constants";

export function useChatScroll({
  containerRef,
  items = [],
  resetDeps = [],
  bottomThreshold = 80,
  initialScroll = true,
  // When true, suppress the hook's own auto scroll-to-bottom (initial load and
  // new-message append). Used for the deep-link flow where useScrollToMessage
  // positions the view on a specific target message — otherwise this hook's
  // initial scrollToBottom + stickToBottom pin drags the view back to bottom.
  disableAutoScroll = false,
  anchorSelector = `.${MESSAGE_CONTAINER_CLASSNAME}`,
  // How to restore anchor after older messages load: "preserve" keeps its relative position; "bottom" pins it to the bottom of viewport
  anchorRestorePosition = "preserve",
  // Strategy to persist position on older loads: 'element' (uses data-message-id) or 'delta' (scrollHeight diff only)
  anchorStrategy = "element",
}) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const prevItemsLenRef = useRef(0);
  const loadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const lastMeasuredScrollHeightRef = useRef(0);
  const graceTimerRef = useRef(null);
  const initialDoneRef = useRef(false);
  const anchorStateRef = useRef({ id: undefined, relativeTop: 0 });
  const anchorChildrenRef = useRef(null); // cached children during load window
  const rafIdRef = useRef(null); // throttle scroll-time anchor updates
  const lastAnchorCaptureTopRef = useRef(-1); // avoid redundant captures
  const pinBottomRef = useRef(false); // while true, hold viewport at bottom
  const wasAtBottomRef = useRef(true); // at-bottom state BEFORE the latest items change
  const settleRafRef = useRef(null); // rAF id for the post-scroll settle loop
  const imgCleanupRef = useRef(null); // detaches image load listeners

  const computeIsAtBottom = () => {
    if (!containerRef?.current) return true;
    const el = containerRef.current;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - bottomThreshold;
  };

  // Find first visible child using cached list (binary search) or live DOM scan
  const captureVisibleAnchor = el => {
    try {
      const children = anchorChildrenRef.current || el.querySelectorAll(anchorSelector);
      if (!children || children.length === 0) return { id: undefined, relativeTop: 0 };

      const targetTop = el.scrollTop;
      let firstVisible = null;

      // Binary search for first element with offsetTop >= targetTop
      let lo = 0;
      let hi = children.length - 1;
      let ansIdx = -1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const nodeTop = children[mid].offsetTop;
        if (nodeTop >= targetTop) {
          ansIdx = mid;
          hi = mid - 1;
        } else {
          lo = mid + 1;
        }
      }
      firstVisible = ansIdx >= 0 ? children[ansIdx] : null;

      if (firstVisible) {
        const id = firstVisible.getAttribute("data-message-id");
        const relativeTop = firstVisible.offsetTop - el.scrollTop;
        return { id, relativeTop };
      }
    } catch (_) {
      // ignore
    }
    return { id: undefined, relativeTop: 0 };
  };

  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

  const pinToBottomNow = () => {
    const el = containerRef?.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // Hold the viewport glued to the bottom while late-loading content grows
  // the thread height *after* the initial scroll. The scroll container has a
  // fixed height and its content grows inside it (MMS/images decoding,
  // avatars, translated-text blocks), so a single scrollToBottom computed
  // against the smaller scrollHeight lands ABOVE the true bottom. We re-pin
  // across a short settle window (synchronous/layout-driven growth) and also
  // on each image load/error (async media on slow networks). The pin is
  // released the moment the user scrolls up (see handleScroll).
  const stickToBottom = ({ rafMs = 600 } = {}) => {
    const el = containerRef?.current;
    if (!el) return;
    pinBottomRef.current = true;

    // 1) rAF settle loop — catches layout-driven height changes that resolve
    //    over the next few frames. Skipped (rafMs = 0) for smooth scrolls so
    //    the animation isn't overridden by per-frame jumps.
    if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);
    if (rafMs > 0) {
      const startedAt = now();
      const step = () => {
        if (!pinBottomRef.current || !containerRef?.current) {
          settleRafRef.current = null;
          return;
        }
        pinToBottomNow();
        if (now() - startedAt < rafMs) {
          settleRafRef.current = requestAnimationFrame(step);
        } else {
          settleRafRef.current = null;
        }
      };
      settleRafRef.current = requestAnimationFrame(step);
    }

    // 2) Image listeners — media that finishes loading after the settle
    //    window still re-pins, but only while the user is still at bottom.
    if (imgCleanupRef.current) imgCleanupRef.current();
    const imgs = Array.from(el.querySelectorAll("img")).filter(img => !img.complete);
    if (imgs.length === 0) {
      imgCleanupRef.current = null;
      return;
    }
    const onImgSettle = () => {
      if (pinBottomRef.current) pinToBottomNow();
    };
    imgs.forEach(img => {
      img.addEventListener("load", onImgSettle);
      img.addEventListener("error", onImgSettle);
    });
    imgCleanupRef.current = () => {
      imgs.forEach(img => {
        img.removeEventListener("load", onImgSettle);
        img.removeEventListener("error", onImgSettle);
      });
      imgCleanupRef.current = null;
    };
  };

  const handleScroll = () => {
    const atBottom = computeIsAtBottom();
    setIsAtBottom(atBottom);
    // Remember the at-bottom state as the user leaves it, so the append
    // branch can decide whether to follow new messages based on where the
    // user was BEFORE the new content grew the thread (measuring after the
    // DOM update would read the post-growth position and wrongly conclude
    // the user had scrolled up by one message).
    wasAtBottomRef.current = atBottom;
    if (atBottom) setUnreadCount(0);
    // User scrolled away from the bottom — release the auto-pin so the
    // settle loop / image listeners stop dragging them back down.
    else pinBottomRef.current = false;

    // While loading older items, continuously follow the first visible message
    if (loadingOlderRef.current && containerRef?.current && anchorStrategy === "element") {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const el = containerRef.current;
        if (!el) return;
        const top = el.scrollTop;
        if (top === lastAnchorCaptureTopRef.current) return;
        lastAnchorCaptureTopRef.current = top;
        anchorStateRef.current = captureVisibleAnchor(el);
      });
    }
  };

  const scrollToBottom = (behavior = "smooth") => {
    if (!containerRef?.current) return;
    const el = containerRef.current;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setIsAtBottom(true);
    wasAtBottomRef.current = true;
    setUnreadCount(0);
    // Hold the bottom through late content growth. Instant scrolls (initial
    // open, opp switch) run the full rAF settle; smooth scrolls only watch
    // images so the animation isn't overridden.
    stickToBottom({ rafMs: behavior === "auto" ? 600 : 0 });
  };

  const safeEscape = value => {
    try {
      // CSS may be undefined in some non-DOM environments
      if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(String(value));
    } catch (_) {
      // ignore
    }
    return String(value).replace(/["'`\\]/g, "\\$&");
  };

  const onLoadOlderStart = () => {
    if (!containerRef?.current) return;
    loadingOlderRef.current = true;
    prevScrollHeightRef.current = containerRef.current.scrollHeight;
    lastMeasuredScrollHeightRef.current = prevScrollHeightRef.current;

    if (anchorStrategy === "element") {
      const el = containerRef.current;
      // Cache children snapshot (live NodeList) for faster anchor lookup during load
      anchorChildrenRef.current = el.querySelectorAll(anchorSelector);
      anchorStateRef.current = captureVisibleAnchor(el);
    } else {
      // delta strategy needs no element capture
      anchorStateRef.current = { id: undefined, relativeTop: 0 };
    }
  };

  // Adjust after items change
  useLayoutEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    // If we are prepending older items, restore position (check this FIRST before initial scroll)
    if (loadingOlderRef.current) {
      const newScrollHeight = el.scrollHeight;
      let restored = false;

      if (anchorStrategy === "element") {
        const { id, relativeTop } = anchorStateRef.current || {};
        if (id) {
          const target = el.querySelector(`${anchorSelector}[data-message-id="${safeEscape(id)}"]`);
          if (target) {
            let desiredScrollTop;
            if (anchorRestorePosition === "bottom") {
              // Align target's bottom to container's bottom
              const containerHeight = el.clientHeight;
              const targetHeight = target.offsetHeight;
              desiredScrollTop = target.offsetTop - (containerHeight - targetHeight);
            } else {
              // Preserve previous offset from top
              desiredScrollTop =
                target.offsetTop - (typeof relativeTop === "number" ? relativeTop : 0);
            }
            // Clamp within bounds
            desiredScrollTop = Math.max(
              0,
              Math.min(desiredScrollTop, el.scrollHeight - el.clientHeight)
            );
            el.scrollTop = desiredScrollTop;
            restored = true;
          }
        }
      }

      // Fallback to height-delta method if element-based failed
      if (!restored) {
        const delta = newScrollHeight - prevScrollHeightRef.current;
        if (delta !== 0) {
          el.scrollTop += delta;
        }
      }

      // Grace period: images/media may increase height after render
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = setTimeout(() => {
        loadingOlderRef.current = false;
        anchorChildrenRef.current = null;
      }, 800);
      lastMeasuredScrollHeightRef.current = newScrollHeight;
      prevItemsLenRef.current = items.length;
      anchorStateRef.current = { id: undefined, relativeTop: 0 };
      return;
    }

    // Initial scroll to bottom on first load (check AFTER loading older to avoid conflicts).
    // Skipped while a deep-link target is active so it doesn't fight useScrollToMessage.
    if (initialScroll && !disableAutoScroll && !initialDoneRef.current && items?.length > 0) {
      // Use auto for the first render to avoid animation
      scrollToBottom("auto");
      initialDoneRef.current = true;
      prevItemsLenRef.current = items.length;
      return;
    }

    // New messages appended: auto-scroll only if the user was at bottom
    // BEFORE this batch arrived (wasAtBottomRef), not after — see handleScroll.
    const lenIncreased = items?.length > prevItemsLenRef.current;
    if (lenIncreased && !disableAutoScroll) {
      if (wasAtBottomRef.current) {
        // Keep user at bottom (auto so the settle guarantees a flush bottom)
        scrollToBottom("auto");
      } else {
        setUnreadCount(count => count + (items.length - prevItemsLenRef.current));
      }
    }

    prevItemsLenRef.current = items?.length || 0;
    // no-op
  }, [items, containerRef]);

  // Resize observer to compensate height changes during older load (e.g., images loading)
  useEffect(() => {
    const el = containerRef?.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (!loadingOlderRef.current) return;
      const newH = el.scrollHeight;
      const delta = newH - lastMeasuredScrollHeightRef.current;
      if (delta !== 0) {
        el.scrollTop += delta;
        lastMeasuredScrollHeightRef.current = newH;
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [containerRef]);

  // Attach native scroll listener
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;
    const onScroll = () => handleScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    // Initialize state
    handleScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [containerRef, bottomThreshold]);

  // Reset when switching threads
  useEffect(() => {
    prevItemsLenRef.current = 0;
    setUnreadCount(0);
    initialDoneRef.current = false;
    loadingOlderRef.current = false;
    wasAtBottomRef.current = true;
    // Tear down any in-flight bottom-pin from the previous thread so it
    // can't fight the new thread's own initial scroll.
    pinBottomRef.current = false;
    if (settleRafRef.current) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }
    if (imgCleanupRef.current) imgCleanupRef.current();
  }, resetDeps); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);
      if (imgCleanupRef.current) imgCleanupRef.current();
    },
    []
  );

  const showScrollToBottom = useMemo(
    () => !isAtBottom && (items?.length || 0) > 0,
    [isAtBottom, items]
  );

  return {
    isAtBottom,
    showScrollToBottom,
    unreadCount,
    scrollToBottom,
    handleScroll,
    onLoadOlderStart,
    setUnreadCount, // exposed just in case
  };
}

export default useChatScroll;
